/**
 * WebSocket Store — Global singleton WS connection state management
 *
 * Manages a single WebSocket connection to game-ws-relay-service.
 * Components subscribe/unsubscribe to lobby and room channels via hooks.
 * Messages are routed to registered handlers.
 *
 * Unsubscribe is deferred via queueMicrotask to avoid a gap during
 * screen transitions (e.g., GameRoomScreen → GameScreen) where the old
 * hook unmounts before the new hook mounts. Without deferral, the relay
 * receives room:unsubscribe + room:subscribe as two messages, and events
 * arriving between them are lost.
 */

import type { GameRoom, WSServerMessage } from "../game-client/types";
import {
  createWSClient,
  deriveWsUrl,
  type WSClient,
  type WSRouteMode,
} from "../game-client/ws-client";

type ConnectionStatus = "disconnected" | "connected" | "reconnecting";

/** Handler for lobby:games_updated messages */
export type LobbyUpdateHandler = (games: GameRoom[]) => void;

/** Handler for room-scoped messages (game:event, game:room_updated) */
export type RoomMessageHandler = (message: WSServerMessage) => void;

/** Handler for connection status changes */
export type ConnectionChangeHandler = (status: ConnectionStatus) => void;

interface WSState {
  readonly status: ConnectionStatus;
  readonly userId: string | null;
}

// ---- Singleton state ----
let client: WSClient | null = null;
let state: WSState = { status: "disconnected", userId: null };
let lastBaseUrl: string | null = null;
let lastRouteMode: WSRouteMode = "v2";

// Subscription tracking
let currentLobbyGameType: string | null = null;
let currentRoomId: string | null = null;

// Deferred unsubscribe flags — cancelled if a new subscribe arrives first
let pendingLobbyUnsubscribe = false;
let pendingRoomUnsubscribe = false;

// Handler registries
const lobbyHandlers = new Set<LobbyUpdateHandler>();
const roomHandlers = new Set<RoomMessageHandler>();
const connectionChangeHandlers = new Set<ConnectionChangeHandler>();

// State change listeners (for useSyncExternalStore)
const listeners = new Set<() => void>();

function notify(): void {
  for (const listener of listeners) {
    listener();
  }
}

function setState(update: Partial<WSState>): void {
  const prev = state;
  state = { ...state, ...update };
  notify();
  if (update.status !== undefined && update.status !== prev.status) {
    for (const handler of connectionChangeHandlers) {
      handler(update.status);
    }
  }
}

function handleMessage(message: WSServerMessage): void {
  switch (message.type) {
    case "connected":
      setState({ userId: message.userId });
      // Re-subscribe after reconnect
      if (currentLobbyGameType !== null) {
        client?.send({
          type: "lobby:subscribe",
          gameType: currentLobbyGameType,
        });
      }
      if (currentRoomId !== null) {
        client?.send({ type: "room:subscribe", roomId: currentRoomId });
      }
      break;

    case "lobby:games_updated":
      for (const handler of lobbyHandlers) {
        handler(message.games);
      }
      break;

    case "game:event":
    case "game:viewer_state":
    case "game:room_updated":
      console.info(
        `[ws-store] ${message.type} handlers=${roomHandlers.size}`,
        message.type === "game:room_updated"
          ? (message as { room?: { currentPlayerCount?: number } }).room
              ?.currentPlayerCount
          : "",
      );
      for (const handler of roomHandlers) {
        handler(message);
      }
      break;

    case "error":
      console.warn("[ws-store] server error:", message.message);
      break;
  }
}

interface WsTestControls {
  __CN_WS_TEST_DISCONNECT__?: () => void;
  __CN_WS_TEST_RECONNECT__?: () => void;
}

function isLocalRuntime(): boolean {
  if (typeof window === "undefined") return false;
  return ["localhost", "127.0.0.1", "::1"].includes(window.location.hostname);
}

function exposeWsTestControls(): void {
  if (!isLocalRuntime()) return;

  // Local E2E can force a single-tab reconnect without relying on browser
  // network emulation. Staging/prod hosts never expose this surface.
  const target = window as Window & WsTestControls;
  target.__CN_WS_TEST_DISCONNECT__ = () => {
    client?.close();
    client = null;
    setState({ status: "reconnecting" });
  };
  target.__CN_WS_TEST_RECONNECT__ = () => {
    if (lastBaseUrl !== null) {
      wsActions.connect(lastBaseUrl, lastRouteMode);
    }
  };
}

// ---- Public API ----

export const wsActions = {
  /** Connect to the WS relay. Call once on app startup (after auth). */
  connect(baseUrl: string, routeMode: WSRouteMode = "v2"): void {
    if (client !== null && lastBaseUrl === baseUrl) {
      lastRouteMode = routeMode;
      return;
    }
    if (client !== null) {
      client.close();
      client = null;
    }

    lastBaseUrl = baseUrl;
    lastRouteMode = routeMode;
    exposeWsTestControls();

    const wsUrl = deriveWsUrl(baseUrl, routeMode);
    client = createWSClient(wsUrl, {
      onMessage: handleMessage,
      onConnect: () => setState({ status: "connected" }),
      onDisconnect: () => setState({ status: "reconnecting" }),
    });

    setState({ status: "reconnecting" }); // Initial connecting state
  },

  /** Disconnect and clean up. */
  disconnect(): void {
    client?.close();
    client = null;
    lastBaseUrl = null;
    lastRouteMode = "v2";
    currentLobbyGameType = null;
    currentRoomId = null;
    pendingLobbyUnsubscribe = false;
    pendingRoomUnsubscribe = false;
    lobbyHandlers.clear();
    roomHandlers.clear();
    connectionChangeHandlers.clear();
    setState({ status: "disconnected", userId: null });
  },

  /** Subscribe to lobby updates for a game type. */
  subscribeLobby(gameType: string, handler: LobbyUpdateHandler): () => void {
    pendingLobbyUnsubscribe = false; // Cancel any pending unsubscribe
    lobbyHandlers.add(handler);
    currentLobbyGameType = gameType;
    client?.send({ type: "lobby:subscribe", gameType });

    return () => {
      lobbyHandlers.delete(handler);
      if (lobbyHandlers.size === 0 && client !== null) {
        pendingLobbyUnsubscribe = true;
        queueMicrotask(() => {
          if (!pendingLobbyUnsubscribe) return;
          pendingLobbyUnsubscribe = false;
          currentLobbyGameType = null;
          client?.send({ type: "lobby:unsubscribe" });
        });
      }
    };
  },

  /**
   * Subscribe to room messages (game:event + game:room_updated).
   *
   * Unsubscribe is deferred via microtask so that screen transitions
   * (unmount old hook → mount new hook) don't create a gap where the
   * relay has no subscriber and drops events.
   */
  subscribeRoom(roomId: string, handler: RoomMessageHandler): () => void {
    pendingRoomUnsubscribe = false; // Cancel any pending unsubscribe
    roomHandlers.add(handler);
    currentRoomId = roomId;
    client?.send({ type: "room:subscribe", roomId });

    return () => {
      roomHandlers.delete(handler);
      if (roomHandlers.size === 0 && client !== null) {
        pendingRoomUnsubscribe = true;
        queueMicrotask(() => {
          if (!pendingRoomUnsubscribe) return;
          pendingRoomUnsubscribe = false;
          currentRoomId = null;
          client?.send({ type: "room:unsubscribe" });
        });
      }
    };
  },

  /** Subscribe to connection status changes. */
  onConnectionChange(handler: ConnectionChangeHandler): () => void {
    connectionChangeHandlers.add(handler);
    return () => connectionChangeHandlers.delete(handler);
  },
};

export const wsStore = {
  getState: (): WSState => state,
  subscribe: (listener: () => void): (() => void) => {
    listeners.add(listener);
    return () => listeners.delete(listener);
  },
};
